/**
 * Pure grouping logic for the two coaching-business library indexes
 * (`/coaching-business` and `/coaching-business/playbooks`), built entirely
 * from the `shelf` / `curriculumOrder` / `startHereOrder` columns each
 * published guide and playbook already carries.
 *
 * The single correctness rule this module exists to enforce: a published row
 * is never invisible. A row lands in whichever rail its `shelf` puts it on —
 * `canon` always routes to the playbooks index, every other shelf value to
 * the guides index — and a row the sheet hasn't shelved yet still renders, in
 * a catch-all section on the index matching its own content type. Nothing
 * here reads a title, slug, description, or per-row URL; it only reads the
 * shelf/order columns and hands back the caller's own row objects untouched,
 * so a route can never drift from what the database actually publishes.
 *
 * Kept framework-free and side-effect-free (no DB import) so the rail/order
 * rules are unit-testable without Postgres. The two route components own
 * rendering — headings, card treatment, links — and derive a row's URL from
 * its `type` discriminant rather than storing one.
 *
 * Aliases: shelf grouping, library rail order, guides index shelves,
 * playbooks index shelves, curriculum order sort, start here shelf
 */

import type { LibraryShelf } from "~/lib/shared/content-status";

/**
 * The minimal shape this module reads off a guide. `slug` is required
 * (non-null) because the caller is expected to have already filtered out
 * slugless rows — those have no destination URL and can't render on either
 * index regardless of shelf.
 */
export interface ShelvableGuide {
  readonly slug: string;
  readonly title: string;
  readonly shelf: LibraryShelf | null;
  readonly curriculumOrder: number | null;
}

/** The minimal shape this module reads off a playbook. */
export interface ShelvablePlaybook {
  readonly slug: string;
  readonly title: string;
  readonly shelf: LibraryShelf | null;
  readonly curriculumOrder: number | null;
  /** Position on the six-slot "Start Here" shelf, 1–6, or null. */
  readonly startHereOrder: number | null;
}

/**
 * A shelved row tagged with its content type and carrying the caller's own
 * (possibly much richer) guide/playbook object, so a route that needs
 * `guideType` for card sizing or `thoughtLeader` for a byline chip gets it
 * back without this module needing to know either field exists.
 */
export type LibraryEntry<
  TGuide extends ShelvableGuide,
  TPlaybook extends ShelvablePlaybook,
> =
  | { readonly type: "guide"; readonly item: TGuide }
  | { readonly type: "playbook"; readonly item: TPlaybook };

/**
 * A section key is either a real `library_shelf` enum value, `"start_here"`
 * for the playbooks index's hand-picked six, or `"unshelved"` for the
 * catch-all that guarantees nothing published goes missing.
 */
export type LibrarySectionKey = LibraryShelf | "start_here" | "unshelved";

export interface LibrarySection<
  TGuide extends ShelvableGuide,
  TPlaybook extends ShelvablePlaybook,
> {
  readonly key: LibrarySectionKey;
  readonly items: readonly LibraryEntry<TGuide, TPlaybook>[];
}

/**
 * Guides-index rail order. `spine_hero` leads (the flagship roadmap), then
 * the four stage shelves in the order a coach's practice graduates through,
 * then `spine_companion` last as a quieter closing band. `canon` is
 * deliberately absent — it is the playbooks index's rail, never this one's.
 */
const GUIDE_RAIL_SHELVES: readonly LibraryShelf[] = [
  "spine_hero",
  "starting_out",
  "getting_consistent",
  "building",
  "scaling",
  "spine_companion",
];

/**
 * Playbooks-index rail order, after the Start Here six. Currently a single
 * shelf, but declared as an array (not one constant check) so the rail order
 * comment stays the source of truth if a second playbooks shelf is ever added.
 */
const PLAYBOOK_RAIL_SHELVES: readonly LibraryShelf[] = ["canon"];

/**
 * Order two same-shelf rows: `curriculumOrder` ascending, nulls last, ties
 * (including null vs. null) broken by title. No caller may pass a different
 * comparator in — this is the one ordering rule both indexes share.
 */
function compareByCurriculumOrder(
  a: { readonly curriculumOrder: number | null; readonly title: string },
  b: { readonly curriculumOrder: number | null; readonly title: string },
): number {
  if (a.curriculumOrder !== null && b.curriculumOrder !== null) {
    return (
      a.curriculumOrder - b.curriculumOrder || a.title.localeCompare(b.title)
    );
  }
  if (a.curriculumOrder !== null) return -1;
  if (b.curriculumOrder !== null) return 1;
  return a.title.localeCompare(b.title);
}

/**
 * Place of a shelf on the guides rail. Rows on no rail shelf (unshelved, or
 * `canon`) sort after every shelved row.
 */
function railPosition(shelf: LibraryShelf | null): number {
  const index = shelf === null ? -1 : GUIDE_RAIL_SHELVES.indexOf(shelf);
  return index === -1 ? GUIDE_RAIL_SHELVES.length : index;
}

/**
 * The library's editorial reading order across shelves: shelf position on the
 * guides rail, then `compareByCurriculumOrder` within the shelf. Use it to pick
 * "the first few" pages from rows that sit on different shelves, where
 * `curriculumOrder` alone (a position within one shelf) would interleave them.
 */
export function compareForReading(
  a: Omit<ShelvableGuide, "slug">,
  b: Omit<ShelvableGuide, "slug">,
): number {
  return (
    railPosition(a.shelf) - railPosition(b.shelf) ||
    compareByCurriculumOrder(a, b)
  );
}

/** Narrows `startHereOrder` from `number | null` to `number`. */
function hasStartHereOrder<TPlaybook extends ShelvablePlaybook>(
  playbook: TPlaybook,
): playbook is TPlaybook & { startHereOrder: number } {
  return playbook.startHereOrder !== null;
}

function toEntries<
  TGuide extends ShelvableGuide,
  TPlaybook extends ShelvablePlaybook,
>(
  guides: readonly TGuide[],
  playbooks: readonly TPlaybook[],
): LibraryEntry<TGuide, TPlaybook>[] {
  return [
    ...guides.map((item): LibraryEntry<TGuide, TPlaybook> => ({
      type: "guide",
      item,
    })),
    ...playbooks.map((item): LibraryEntry<TGuide, TPlaybook> => ({
      type: "playbook",
      item,
    })),
  ];
}

function buildRailSections<
  TGuide extends ShelvableGuide,
  TPlaybook extends ShelvablePlaybook,
>(
  entries: readonly LibraryEntry<TGuide, TPlaybook>[],
  rail: readonly LibraryShelf[],
): LibrarySection<TGuide, TPlaybook>[] {
  return rail.flatMap((shelf) => {
    const items = entries
      .filter((entry) => entry.item.shelf === shelf)
      .sort((a, b) => compareByCurriculumOrder(a.item, b.item));
    return items.length > 0 ? [{ key: shelf, items }] : [];
  });
}

/**
 * Shelves for `/coaching-business`: the five non-Canon rail shelves in
 * curriculum order, then an "unshelved" catch-all of any guide the sheet
 * hasn't assigned a shelf to yet. A playbook shelved onto a guide-rail shelf
 * (e.g. `mom-test-coaches` on `starting_out`) renders inside that shelf like
 * any guide would; a guide shelved onto `canon` is not this rail's concern —
 * it surfaces on the playbooks index instead, by the same rule in reverse.
 */
export function buildGuidesIndexSections<
  TGuide extends ShelvableGuide,
  TPlaybook extends ShelvablePlaybook,
>(
  guides: readonly TGuide[],
  playbooks: readonly TPlaybook[],
): LibrarySection<TGuide, TPlaybook>[] {
  const entries = toEntries(guides, playbooks);
  const railSections = buildRailSections(entries, GUIDE_RAIL_SHELVES);

  const unshelved = entries
    .filter((entry) => entry.type === "guide" && entry.item.shelf === null)
    .sort((a, b) => compareByCurriculumOrder(a.item, b.item));

  return unshelved.length > 0
    ? [...railSections, { key: "unshelved", items: unshelved }]
    : railSections;
}

/**
 * Sections for `/coaching-business/playbooks`: the Start Here six (ordered by
 * `startHereOrder`), then the Canon rail in curriculum order, then an
 * "unshelved" catch-all of any playbook that landed on neither. A row that
 * holds both a Start Here slot and a guide-rail shelf (again
 * `mom-test-coaches`) still appears in Start Here here — that slot is
 * independent of `shelf` — while its single "home" shelf is the guides index.
 */
export function buildPlaybooksIndexSections<
  TGuide extends ShelvableGuide,
  TPlaybook extends ShelvablePlaybook,
>(
  guides: readonly TGuide[],
  playbooks: readonly TPlaybook[],
): LibrarySection<TGuide, TPlaybook>[] {
  const entries = toEntries(guides, playbooks);

  const startHerePlaybooks = playbooks
    .filter(hasStartHereOrder)
    .sort(
      (a, b) =>
        a.startHereOrder - b.startHereOrder || a.title.localeCompare(b.title),
    );
  // Widened to TPlaybook (dropping the narrowed startHereOrder) so it can be
  // compared against `entry.item` below, which carries the unnarrowed type.
  const onStartHere = new Set<TPlaybook>(startHerePlaybooks);
  const startHereEntries: LibraryEntry<TGuide, TPlaybook>[] =
    startHerePlaybooks.map((item) => ({ type: "playbook", item }));

  const railSections = buildRailSections(entries, PLAYBOOK_RAIL_SHELVES);

  const unshelved = entries
    .filter(
      (entry) =>
        entry.type === "playbook" &&
        entry.item.shelf === null &&
        !onStartHere.has(entry.item),
    )
    .sort((a, b) => compareByCurriculumOrder(a.item, b.item));

  const sections: LibrarySection<TGuide, TPlaybook>[] = [];
  if (startHereEntries.length > 0) {
    sections.push({ key: "start_here", items: startHereEntries });
  }
  sections.push(...railSections);
  if (unshelved.length > 0) {
    sections.push({ key: "unshelved", items: unshelved });
  }
  return sections;
}
